import React, { useCallback, memo, useId } from 'react';
import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import ReactSwitch, { ReactSwitchProps } from 'react-switch';
import _get from 'lodash/get';
import { useFormikError, clearError } from './FormikFields';
import { Theme } from '@app/theme';
import { useFormikContext } from 'formik';
import { TooltipText } from '@app/core';
import { useTrack } from '@app/analytics';

const Switch = memo(ReactSwitch);
const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    marginBottom: theme.marginBase * 3,
    marginTop: theme.marginBase * 3,
    width: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.marginBase * 2,
  },
  textsContainer: {
    flex: 1,
  },
  label: {
    ...theme.fonts.label,
    marginBottom: theme.marginBase / 2,
  },
  explanation: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
  },
  error: {
    ...theme.fonts.caption,
    color: theme.colors.red,
  },
  disabled: {
    opacity: 0.5,
    cursor: 'auto',
  },
  noMargin: {
    marginBottom: 0,
    marginTop: 0,
  },
}));

interface Props extends Partial<ReactSwitchProps> {
  containerClass?: string;
  label?: string | React.ReactNode;
  explanation?: string;
  error?: string;
  name?: string;
  checked?: boolean;
  children?: React.ReactNode;
  tooltip?: string;
  disabledTooltip?: string;
  hideChildrenIfNotChecked?: boolean;
  onToggle?: (checked: boolean, name?: string) => any;
  labelBefore?: boolean;
  noMargin?: boolean;
}

export const Toggle = (props: Props) => {
  const {
    containerClass,
    label,
    labelBefore,
    error,
    className,
    explanation,
    name,
    checked,
    children,
    hideChildrenIfNotChecked,
    tooltip,
    disabledTooltip,
    onChange,
    onToggle,
    noMargin,
    ...rest
  } = props;

  // @ts-ignore
  const theme: Theme = useTheme();
  const classes = useStyles({ ...props, theme });
  const errorMsg = useFormikError({ name, ...props });
  const formik = useFormikContext<any>();

  const generatedId = useId();
  const id = rest.id || `toggle-${generatedId}`;

  const setFieldValue = formik && formik.setFieldValue;
  const formikStatus = formik && formik.status;

  const track = useTrack();

  const _onChange = useCallback(
    (checked: boolean, event: any, id: string) => {
      if (onToggle) {
        onToggle(checked, name);
      }
      if (setFieldValue && name) {
        setFieldValue(name, checked);
        clearError(formikStatus, name);
      } else {
        onChange && onChange(checked, event, id);
      }
      const analyticsName = name || id || label || 'Toggle';
      track(`${analyticsName} Changed`, {
        label: label && typeof label === 'string' ? label : undefined,
      });
    },
    [setFieldValue, formikStatus, onChange, name, track, label, onToggle],
  );
  const value = (formik && name ? _get(formik.values, name) : checked) || false;

  const labelRendered = (
    <div className={classes.textsContainer}>
      <label
        htmlFor={id}
        className={classnames({
          [classes.disabled]: rest.disabled,
        })}
      >
        <div className={classes.label}>{label}</div>
        {!!explanation && (
          <div className={classes.explanation}>{explanation}</div>
        )}
      </label>
      {(error || errorMsg) && (
        <div className={classes.error}>{error || errorMsg}</div>
      )}
      {hideChildrenIfNotChecked && !value ? null : children}
    </div>
  );

  return (
    <div
      className={classnames(containerClass, classes.container, {
        [classes.noMargin]: noMargin,
      })}
    >
      {!!labelBefore && labelRendered}
      <TooltipText title={rest.disabled ? disabledTooltip : tooltip}>
        <div>
          <Switch
            className={className}
            checked={value}
            onChange={_onChange}
            onColor={theme.colors.green}
            offColor={theme.colors.grayBorder}
            activeBoxShadow=""
            boxShadow=""
            width={theme.height.regular}
            height={theme.height.regular / 2}
            checkedIcon={false}
            uncheckedIcon={false}
            id={id}
            {...rest}
          />
        </div>
      </TooltipText>
      {!labelBefore && labelRendered}
    </div>
  );
};
